import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  archiveUser,
  changeUserEmail,
  createUser,
  fetchAccountLeads,
  fetchUserClerkAccount,
  fetchUsers,
  markUserEmailVerified,
  resendInvitation,
  startImpersonation,
  updateUserAccountLead,
  updateUserGroup,
  updateUserName,
  updateUserRole,
} from "@/api/users";
import type { User, UserListParams, UserListResponse } from "@/interfaces/user";
import type { Role } from "@/lib/roles";

/** Outside the ["users"] prefix on purpose: list updaters run setQueriesData(["users"]) on every match. */
export const userClerkAccountKey = (id: number) => ["user-clerk-account", id] as const;

export function useUsers(
  params?: UserListParams & {
    organizationId?: number;
    sortBy?: string;
    sortDir?: "asc" | "desc";
    page?: number;
    limit?: number;
  },
) {
  return useQuery({
    queryKey: ["users", params],
    queryFn: () => fetchUsers(params),
  });
}

export function mergeUserIntoCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  user: User | undefined,
) {
  if (!user?.id) return;

  queryClient.setQueriesData<UserListResponse>({ queryKey: ["users"] }, (old) => {
    if (!old) return old;

    const existingIndex = old.data.findIndex((u) => u.id === user.id);
    if (existingIndex >= 0) {
      const data = [...old.data];
      data[existingIndex] = user;
      return { ...old, data };
    }

    return old;
  });
}

function addUserToCaches(queryClient: ReturnType<typeof useQueryClient>, user: User | undefined) {
  if (!user?.id) return;

  queryClient.setQueriesData<UserListResponse>({ queryKey: ["users"] }, (old) => {
    if (!old) return old;

    if (old.data.some((u) => u.id === user.id)) {
      return {
        ...old,
        data: old.data.map((u) => (u.id === user.id ? user : u)),
      };
    }

    const page = old.page ?? 1;
    if (page !== 1) return old;

    const data = [user, ...old.data];
    if (data.length > old.limit) {
      data.pop();
    }

    return {
      ...old,
      data,
      total: old.total + 1,
      totalPages: Math.ceil((old.total + 1) / old.limit),
    };
  });
}

export function useAccountLeads(enabled = true) {
  return useQuery({
    queryKey: ["account-leads"],
    queryFn: fetchAccountLeads,
    // The list is GFG-only (edit USER @ all); skip the request for non-GFG callers.
    enabled,
  });
}

export function useStartImpersonation() {
  return useMutation({
    mutationFn: ({ targetUserId, actorId }: { targetUserId: string; actorId?: string }) =>
      startImpersonation(targetUserId, actorId),
  });
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    // role: a Role assigns (level inferred server-side); null clears the membership at
    // `level` ("org" | "all"). The returned user carries the fresh orgRole/gfgRole.
    mutationFn: ({
      userId,
      role,
      level,
    }: {
      userId: number;
      role: Role | null;
      level?: "org" | "all";
    }) => updateUserRole(userId, role, level),

    onSuccess: (data) => {
      mergeUserIntoCaches(queryClient, data.user);
    },
  });
}

export function useUpdateUserName() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      firstName,
      lastName,
    }: {
      userId: number;
      firstName: string;
      lastName: string;
    }) => updateUserName(userId, firstName, lastName),

    onSuccess: (data) => {
      mergeUserIntoCaches(queryClient, data.user);
    },
  });
}

export function useUpdateUserGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, groupId }: { userId: number; groupId: number }) =>
      updateUserGroup(userId, groupId),

    onSuccess: (data) => {
      mergeUserIntoCaches(queryClient, data.user);
    },
  });
}

export function useUpdateUserAccountLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, isAccountLead }: { userId: number; isAccountLead: boolean }) =>
      updateUserAccountLead(userId, isAccountLead),

    onMutate: async ({ userId, isAccountLead }) => {
      await queryClient.cancelQueries({ queryKey: ["users"] });

      const previousEntries = queryClient.getQueriesData<UserListResponse>({
        queryKey: ["users"],
      });

      queryClient.setQueriesData<UserListResponse>({ queryKey: ["users"] }, (old) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data.map((u) => (u.id === userId ? { ...u, isAccountLead } : u)),
        };
      });

      return { previousEntries };
    },

    onError: (_err, _variables, context) => {
      context?.previousEntries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },

    onSuccess: (data) => {
      mergeUserIntoCaches(queryClient, data.user);
    },
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      email: string;
      role: Role;
      firstName: string;
      lastName?: string;
      groupId?: number;
      organizationId?: number;
    }) => createUser(data),

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["users"] });
    },

    onSuccess: (data) => {
      addUserToCaches(queryClient, data.user);
    },
  });
}

export function useArchiveUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: number) => archiveUser(userId),

    onMutate: async (userId) => {
      await queryClient.cancelQueries({ queryKey: ["users"] });

      const previousEntries = queryClient.getQueriesData<UserListResponse>({
        queryKey: ["users"],
      });

      queryClient.setQueriesData<UserListResponse>({ queryKey: ["users"] }, (old) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data.filter((u) => u.id !== userId),
          total: Math.max(0, old.total - 1),
        };
      });

      return { previousEntries };
    },

    onError: (_err, _variables, context) => {
      context?.previousEntries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
  });
}

export function useResendInvitation() {
  return useMutation({
    mutationFn: (userId: number) => resendInvitation(userId),
  });
}

const CLERK_ACCOUNT_STALE_MS = 30_000;

export function useUserClerkAccount(userId: number, enabled: boolean) {
  return useQuery({
    queryKey: userClerkAccountKey(userId),
    queryFn: () => fetchUserClerkAccount(userId),
    enabled,
    retry: false,
    staleTime: CLERK_ACCOUNT_STALE_MS,
  });
}

export function useMarkEmailVerified() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: number) => markUserEmailVerified(userId),

    onSuccess: (response, userId) => {
      queryClient.setQueryData(userClerkAccountKey(userId), response.account);
    },
  });
}

export function useChangeUserEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, email }: { userId: number; email: string }) =>
      changeUserEmail(userId, email),

    onSuccess: (data) => {
      mergeUserIntoCaches(queryClient, data.user);
      queryClient.invalidateQueries({ queryKey: userClerkAccountKey(data.user.id) });
    },
  });
}
